/**
 * Lieferzeit-Anzeige für alle Artikel dieses Entwurfs (Maske „Für alle einstellen",
 * Matze 15.10.2026). Vorbelegt mit der Vorgabe der Marke (Hersteller-Seite); eine Auswahl
 * hier gilt nur für diesen Entwurf. Pfade OHNE `/api/`-Prefix.
 */
import { useEffect, useState } from 'react';
import { Truck } from 'lucide-react';
import { api } from '@libero/core/services/api';
import Info from './Info';

interface Stand { vorgabe: number | null; lieferzeitId: number | null; stufen: Array<{ id: number; name: string }> }

export default function LieferzeitFeld({ entwurfId }: { entwurfId: number }) {
  const [stand, setStand] = useState<Stand | null>(null);
  const [meldung, setMeldung] = useState<{ text: string; fehler: boolean } | null>(null);

  useEffect(() => {
    api.get<{ data: Stand }>(`/programmes/artikelimport/entwuerfe/${entwurfId}/lieferzeit`)
      .then(a => setStand(a.data.data))
      .catch(e => setMeldung({ text: e?.response?.data?.error?.message ?? 'Lieferzeit ließ sich nicht laden.', fehler: true }));
  }, [entwurfId]);

  const setzen = async (wert: string) => {
    setMeldung(null);
    try {
      const r = await api.patch<{ data: { lieferzeitId: number | null } }>(
        `/programmes/artikelimport/entwuerfe/${entwurfId}/lieferzeit`, { lieferzeitId: wert === '' ? null : Number(wert) },
      );
      setStand(s => (s ? { ...s, lieferzeitId: r.data.data.lieferzeitId } : s));
      setMeldung({ text: 'Gespeichert — gilt für alle Artikel dieses Entwurfs.', fehler: false });
    } catch (e: any) {
      setMeldung({ text: e?.response?.data?.error?.message ?? 'Speichern fehlgeschlagen.', fehler: true });
    }
  };

  const name = (id: number | null) => (id == null ? '–' : `${id} · ${stand?.stufen.find(s => s.id === id)?.name ?? `Stufe ${id}`}`);
  return (
    <div className="space-y-1 rounded border border-[#DAD9D9] bg-white p-3">
      <h3 className="flex items-center gap-2 text-sm font-medium text-[#1A1A1A]">
        <Truck className="h-4 w-4" aria-hidden /> Lieferzeit-Anzeige
        <Info titel="Woher kommt der Wert?">
          <p>Vorgabe ist die Einstellung der Marke (Hersteller-Seite). Hier lässt sie sich für alle Artikel dieses Entwurfs ändern.</p>
          <p>Gesetzt wird sie beim Anlegen an jeder Variante. Plenty kann den Wert später aus dem Bestand nachrechnen.</p>
        </Info>
      </h3>
      {stand && (
        <select
          value={stand.lieferzeitId ?? ''}
          onChange={e => void setzen(e.target.value)}
          className="rounded border border-[#DAD9D9] px-2 py-1 text-sm"
        >
          <option value="">Vorgabe der Marke ({name(stand.vorgabe)})</option>
          {stand.stufen.map(s => <option key={s.id} value={s.id}>{s.id} · {s.name}</option>)}
        </select>
      )}
      {meldung && <p className={`text-xs ${meldung.fehler ? 'text-red-800' : 'text-green-800'}`}>{meldung.text}</p>}
    </div>
  );
}
